import { supabase } from './supabase';
import { User } from '../types/auth';

// Complete user data cache - stores full user objects instead of just tokens
const userDataCache = new Map<string, { userData: User; timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes cache
const pendingUserCalls = new Map<string, Promise<User>>();

// Function availability cache
let functionAvailable: boolean | null = null;

// Temporary flag to disable slow functions and force fallback mode
const FORCE_FALLBACK_MODE = false; // Changed to false to test optimized functions

// Reduced timeouts for faster auth experience
const FALLBACK_TIMEOUT = 5000; // Increased to 5 seconds for better reliability
const MAX_RETRIES_FALLBACK = 2; // Increased retries for better reliability

// Database connection health tracking
let lastHealthCheck = 0;
let isDatabaseHealthy = true;

// Simple getter/setter for function availability
const setFunctionAvailable = (available: boolean) => {
  functionAvailable = available;
  console.log(`📋 Database functions marked as: ${available ? 'Available' : 'Not Available'}`);
};

// Get cached user data
const getCachedUserData = (userId: string): User | null => {
  const cached = userDataCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('👤 Using cached user data for user:', userId);
    return cached.userData;
  }
  return null;
};

// Set cached user data
const setCachedUserData = (userId: string, userData: User): void => {
  userDataCache.set(userId, { userData, timestamp: Date.now() });
};

// Clear cache for a user
const clearUserDataCache = (userId: string): void => {
  userDataCache.delete(userId);
  pendingUserCalls.delete(userId);
};

// Check database health with caching
const checkDatabaseHealthCached = async (): Promise<boolean> => {
  const now = Date.now();
  if (now - lastHealthCheck < 30000) { // Cache health check for 30 seconds
    return isDatabaseHealthy;
  }
  
  try {
    const { error } = await supabase
      .from('users')
      .select('count')
      .limit(1);
    
    lastHealthCheck = now;
    isDatabaseHealthy = !error;
    console.log(`🏥 Database health check: ${isDatabaseHealthy ? '✅ Healthy' : '❌ Unhealthy'}`);
    return isDatabaseHealthy;
  } catch (error) {
    lastHealthCheck = now;
    isDatabaseHealthy = false;
    console.log('❌ Database health check failed:', error);
    return false;
  }
};

// Retry utility with exponential backoff
const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error = new Error('Unknown error');
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Check if it's a timeout error that we should retry
      const errorMessage = (error as Error).message.toLowerCase();
      if (!errorMessage.includes('timeout') && !errorMessage.includes('network')) {
        throw error; // Don't retry non-timeout errors
      }
      
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`🔄 Retry attempt ${attempt + 1}/${maxRetries + 1} after ${delay}ms delay`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Database query with timeout wrapper
const queryWithTimeout = async <T>(
  queryFunction: () => Promise<T>,
  timeoutMs: number = 8000 // Reduced from 12000 for faster failure detection
): Promise<T> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Database query timeout')), timeoutMs);
  });

  return Promise.race([queryFunction(), timeoutPromise]);
};

export const userService = {
  // Single call to get complete user data (auth + tokens)
  async getCompleteUserData(userId: string, email?: string): Promise<User> {
    // Check cache first
    const cachedUserData = getCachedUserData(userId);
    if (cachedUserData) {
      return cachedUserData;
    }
    
    // Check if there's already a pending call for this user (debounce)
    const existingCall = pendingUserCalls.get(userId);
    if (existingCall) {
      console.log('🔄 Reusing existing user data call for user:', userId);
      return existingCall;
    }
    
    console.log('🏦 Fetching complete user data for user ID:', userId);
    
    // Create a new promise for this call
    const userDataPromise = this.fetchCompleteUserDataInternal(userId, email);
    pendingUserCalls.set(userId, userDataPromise);
    
    try {
      const userData = await userDataPromise;
      console.log('✅ Successfully fetched user data:', { id: userData.id, tokens: userData.tokens });
      return userData;
    } finally {
      // Clean up the pending call
      pendingUserCalls.delete(userId);
    }
  },

  // Internal method to actually fetch complete user data
  async fetchCompleteUserDataInternal(userId: string, email?: string): Promise<User> {
    try {
      // Check database health first
      const isHealthy = await checkDatabaseHealthCached();
      if (!isHealthy) {
        console.warn('⚠️ Database appears unhealthy, using basic fallback');
        return {
          id: userId,
          email: email || '',
          tokens: 0
        };
      }

      const userData = await retryOperation(async () => {
        // Check if we should force fallback mode (skip slow database functions)
        if (FORCE_FALLBACK_MODE) {
          console.log('⚡ FORCE_FALLBACK_MODE enabled - skipping to direct table query');
          // Skip to fallback logic below - don't throw error
        } else {
          // If we haven't determined function availability yet, try the optimized function first
          if (functionAvailable === null || functionAvailable === true) {
            try {
              console.log('🚀 Attempting optimized database function');
              const { data, error } = await queryWithTimeout(async () => {
                return supabase.rpc('get_or_create_user_data', {
                  user_id: userId,
                  user_email: email || null
                });
              }, 8000);

              if (error) {
                if (error.message.includes('function') && error.message.includes('does not exist')) {
                  setFunctionAvailable(false);
                  throw new Error('FUNCTION_NOT_AVAILABLE');
                } else {
                  throw error;
                }
              }

              // Mark function as available and return the data
              setFunctionAvailable(true);
              console.log('✅ Got user data from optimized function');

              // Supabase RPC that returns TABLE yields an array; handle both array/object
              const userRecord: any = Array.isArray(data) ? (data as any[])[0] : (data as any);

              // If RPC doesn't include is_admin, fetch it from users table
              let isAdminFlag: boolean = Boolean(userRecord?.is_admin);
              if (typeof (userRecord as any)?.is_admin === 'undefined') {
                try {
                  const { data: adminRow } = await queryWithTimeout(async () => {
                    return supabase
                      .from('users')
                      .select('is_admin')
                      .eq('id', userId)
                      .single();
                  }, FALLBACK_TIMEOUT);
                  isAdminFlag = Boolean((adminRow as any)?.is_admin);
                } catch (_) {
                  // Ignore; default false
                }
              }

              return {
                id: userRecord?.id ?? userId,
                email: userRecord?.email || email || '',
                tokens: Number(userRecord?.tokens) || 0,
                is_admin: isAdminFlag,
                user_metadata: {
                  full_name: userRecord?.full_name || '',
                  avatar_url: userRecord?.avatar_url || ''
                }
              };
            } catch (functionError) {
              // If function not available, fall back to direct table query
              if ((functionError as Error).message === 'FUNCTION_NOT_AVAILABLE') {
                console.log('🔄 Function not available, using fallback: querying users table directly');
              } else {
                throw functionError;
              }
            }
          }
        }

        // Fallback method using existing tables (or forced fallback)
        if (FORCE_FALLBACK_MODE) {
          console.log('🔄 Using forced fallback: querying users table directly');
        } else {
          console.log('🔄 Using fallback: querying users table directly');
        }
        
        const { data: userData, error: userError } = await queryWithTimeout(async () => {
          return supabase
            .from('users')
            .select('id, email, tokens, is_admin, full_name, avatar_url, created_at, updated_at')
            .eq('id', userId)
            .single();
        }, FALLBACK_TIMEOUT); // Shorter timeout for simple table query

        if (userError) {
          if (userError.code === 'PGRST116') {
            console.log('⏳ User record not found, returning basic user with 0 tokens');
            return {
              id: userId,
              email: email || '',
              tokens: 0
            };
          }
          throw userError;
        }

        console.log('📊 Fallback query result:', userData);

        return {
          id: userData.id,
          email: userData.email || email || '',
          tokens: userData.tokens || 0,
          is_admin: userData.is_admin ?? false,
          user_metadata: {
            full_name: userData.full_name || '',
            avatar_url: userData.avatar_url || ''
          }
        };
      }, MAX_RETRIES_FALLBACK); // Reduced retries for faster failure
      
      // Cache the result
      setCachedUserData(userId, userData);
      
      console.log('✅ Final user data being returned:', userData);
      
      return userData;
    } catch (error) {
      console.error('💥 Exception in getCompleteUserData:', error);
      console.warn('⚠️ Returning user with 0 tokens due to exception');
      
      // Return a basic user object with 0 tokens as fallback
      const fallbackUser: User = {
        id: userId,
        email: email || '',
        tokens: 0
      };
      
      return fallbackUser;
    }
  },

  // Update user tokens (when tokens are spent or purchased)
  async updateUserTokens(userId: string, newTokenBalance: number): Promise<void> {
    // Update cache immediately for instant UI updates
    const cachedUser = getCachedUserData(userId);
    if (cachedUser) {
      const updatedUser = { ...cachedUser, tokens: newTokenBalance };
      setCachedUserData(userId, updatedUser);
    }
  },

  // Clear cache when tokens are spent to ensure fresh data on next fetch
  clearUserCache(userId: string): void {
    clearUserDataCache(userId);
  },

  // Get cache statistics for debugging
  getCacheStats(): { size: number; entries: Array<{ userId: string; age: number }>; pendingCalls: number } {
    const entries = Array.from(userDataCache.entries()).map(([userId, data]) => ({
      userId,
      age: Date.now() - data.timestamp
    }));
    
    return {
      size: userDataCache.size,
      entries,
      pendingCalls: pendingUserCalls.size
    };
  },

  // Check database health with detailed information
  async checkDatabaseHealth(): Promise<{ healthy: boolean; latency: number; error?: string }> {
    const startTime = Date.now();
    try {
      await queryWithTimeout(async () => {
        return supabase.from('users').select('count').limit(1);
      }, FALLBACK_TIMEOUT);
      
      const latency = Date.now() - startTime;
      return { healthy: true, latency };
    } catch (error) {
      const latency = Date.now() - startTime;
      return { 
        healthy: false, 
        latency, 
        error: (error as Error).message 
      };
    }
  },

  // Reset function availability check
  resetFunctionCheck(): void {
    functionAvailable = null;
    console.log('🔄 Function availability check reset');
  },

  // Get current function status
  getFunctionStatus(): { available: boolean | null; checking: boolean } {
    return {
      available: functionAvailable,
      checking: functionAvailable === null
    };
  }
}; 